// ============================================================
// SyncStatusCard — visar "Senast syncad" per kategori (Feature 1).
// Lyssnar pa years/{year}-doc via subscribeYear och renderar tre rader:
//   - Kundfakturor      YYYY-MM-DD HH:mm   N filer  (kalla)
//   - Leverantorsfakturor ...
//   - Utlagg ...
// Visas i Import-vyn (overst) och som badge i bolag-valjaren.
// ============================================================

import { useEffect, useState } from 'react'
import { subscribeYear, type CloudYearDoc, type CategorySyncStatus } from '../lib/cloudStore'
import type { ReceiptCategory } from '../lib/receipts'

const CATEGORY_META: { id: ReceiptCategory; label: string; accent: string }[] = [
  { id: 'kundfakturor',        label: 'Kundfakturor',           accent: '#10b981' },
  { id: 'leverantorsfakturor', label: 'Leverantörsfakturor', accent: '#ef4444' },
  { id: 'utlagg',              label: 'Utlägg',              accent: '#3b82f6' },
]

const SOURCE_LABEL: Record<CategorySyncStatus['source'], string> = {
  'manual-upload':   'manuell',
  'dropbox-scanner': 'dropbox',
  'sie-import':      'SIE',
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

interface Props {
  companyId: string
  companyName?: string
  year: number
  /** Kompakt-lge: liten badge istllet fr fullt kort */
  compact?: boolean
}

export function SyncStatusCard({ companyId, companyName, year, compact = false }: Props) {
  const [doc, setDoc] = useState<CloudYearDoc | null>(null)

  useEffect(() => {
    try {
      return subscribeYear(companyId, year, setDoc)
    } catch { return () => {} }
  }, [companyId, year])

  const sync = doc?.syncStatus ?? {}

  if (compact) {
    // Hitta senaste sync ver alla kategorier
    const allTimes = Object.values(sync)
      .filter((s): s is CategorySyncStatus => !!s)
      .map((s) => new Date(s.lastSyncAt).getTime())
      .filter((t) => !isNaN(t))
    if (allTimes.length === 0) {
      return (
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {'— aldrig syncad —'}
        </span>
      )
    }
    const latest = new Date(Math.max(...allTimes)).toISOString()
    return (
      <span
        title={'Senaste sync: ' + fmtTime(latest)}
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          background: 'rgba(255,255,255,0.04)',
          padding: '2px 6px',
          borderRadius: 4,
        }}
      >
        {'Senast: ' + fmtTime(latest)}
      </span>
    )
  }

  return (
    <section
      className="card"
      style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <div className="card__label" style={{ margin: 0 }}>
          {'Senast syncad' + (companyName ? ' — ' + companyName : '') + ' · År ' + year}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {CATEGORY_META.map((c) => {
          const s = sync[c.id]
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                fontSize: '0.85rem',
                padding: '0.3rem 0.5rem',
                background: 'var(--bg-card-alt, rgba(255,255,255,0.02))',
                borderRadius: 6,
              }}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: s ? c.accent : 'var(--border)',
                  flexShrink: 0,
                }}
              />
              <span style={{ minWidth: 160, fontWeight: 500 }}>{c.label}</span>
              {s ? (
                <>
                  <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtTime(s.lastSyncAt)}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {s.fileCount + ' fil' + (s.fileCount === 1 ? '' : 'er')}
                  </span>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      color: 'var(--text-muted)',
                      background: 'rgba(255,255,255,0.04)',
                      padding: '1px 6px',
                      borderRadius: 4,
                      marginLeft: 'auto',
                    }}
                  >
                    {SOURCE_LABEL[s.source]}
                  </span>
                </>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {'— aldrig —'}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
